// Libraries
import React from 'react';

// Supermove
import {Space, Styled, Icon} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {Typography, colors} from '@supermove/styles';
import {Phone} from '@supermove/utils';

// App
import Button from '@shared/design/components/Button';
import DropdownButton from '@shared/design/components/Button/DropdownButton';
import SectionedDropdownButton from '@shared/design/components/Button/SectionedDropdownButton';
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import Drawer from '@shared/design/components/Drawer';
import FieldInput from '@shared/design/components/Field/FieldInput';
import OutgoingCallDrawerBody from '@shared/modules/Call/components/OutgoingCallDrawerBody';
import PhoneNumberProvider from '@shared/modules/Phone/enums/PhoneNumberProvider';

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
  overflow: visible;
  justify-content: center;
`;

const BodyText = Styled.Text`
  ${Typography.Responsive.Body}
`;

const Column = Styled.View`
  flex: 1;
`;

const getActions = ({viewer, handleSelectedOutboundNumber}: any) => {
  const businessNumbers = viewer.viewingOrganization.verifiedBusinessPhoneNumbers.map(
    (phoneNumber: any) => ({
      text: Phone.display(phoneNumber.number),
      description: phoneNumber.name,
      onPress: () => {
        handleSelectedOutboundNumber(phoneNumber);
      },
    }),
  );

  const userOutboundNumbers = viewer.verifiedPhoneNumbers.map((phoneNumber: any) => ({
    text: Phone.display(phoneNumber.number),
    description: PhoneNumberProvider.convertProviderToDisplay(phoneNumber.provider),
    onPress: () => {
      handleSelectedOutboundNumber(phoneNumber);
    },
  }));

  return {
    businessNumbers,
    userOutboundNumbers,
  };
};

const getDropdownOptions = ({viewer, handleSelectedOutboundNumber}: any) => {
  const actions = getActions({viewer, handleSelectedOutboundNumber});

  const dropdownOptions = [];
  if (actions.businessNumbers.length) {
    dropdownOptions.push({
      name: 'Business Numbers',
      actions: [...actions.businessNumbers],
    });
  }
  if (actions.userOutboundNumbers.length) {
    dropdownOptions.push({
      name: 'Your Outbound Numbers',
      actions: [...actions.userOutboundNumbers],
    });
  }
  return dropdownOptions;
};

interface OutgoingCallDrawerContentProps {
  search: string;
  handleUpdateSearch: (value: string) => void;
  filteredSalesLeads: any;
  loading: boolean;
  handlePhoneNumberSelected: (value: any) => void;
  submitting: boolean;
  form: any;
  responsive: any;
  handleSubmit: () => void;
  viewer: any;
  handleSelectedOutboundNumber: (value: any) => void;
  selectedOutboundNumber: string;
  selectedSalesLeadName: string;
  selectedSalesLeadId: string;
}

const OutgoingCallDrawerContent = ({
  search,
  handleUpdateSearch,
  filteredSalesLeads,
  loading,
  handlePhoneNumberSelected,
  submitting,
  form,
  responsive,
  handleSubmit,
  viewer,
  handleSelectedOutboundNumber,
  selectedOutboundNumber,
  selectedSalesLeadName,
  selectedSalesLeadId,
}: OutgoingCallDrawerContentProps) => {
  const dropdownProps = {
    text: selectedOutboundNumber ? Phone.display(selectedOutboundNumber) : 'Select Outbound Number',
    menuPosition: DropdownButton.MENU_POSITION.TOP,
    isSmall: false,
    menuStyle: {minWidth: 300},
    showDescriptionInOption: true,
    isResponsive: true,
    sheetLabel: 'Select Outbound Number',
    maxDropdownHeight: 500,
    isSearchable: true,
  };

  const dropdownOptions = getDropdownOptions({viewer, handleSelectedOutboundNumber});

  const outboundErrorMessage = form.errors.initiateOutgoingCallForm?.outboundPhoneNumberId;

  return (
    <React.Fragment>
      <Drawer.Body bodyScrollStyle={{flex: 1}}>
        <OutgoingCallDrawerBody
          search={search}
          handleUpdateSearch={handleUpdateSearch}
          filteredSalesLeads={filteredSalesLeads}
          loading={loading}
          handlePhoneNumberSelected={handlePhoneNumberSelected}
          submitting={submitting}
          form={form}
          selectedSalesLeadName={selectedSalesLeadName}
          selectedSalesLeadId={selectedSalesLeadId}
          responsive={responsive}
        />
      </Drawer.Body>
      <Drawer.FooterContainer>
        <Column>
          <Row>
            <BodyText responsive={responsive} style={{color: colors.gray.secondary}}>
              My Caller ID:
            </BodyText>
            <Space width={8} />
            <SectionedDropdownButton
              {...dropdownProps}
              sections={
                responsive.desktop
                  ? dropdownOptions
                  : dropdownOptions.map((section: any) => ({
                      ...section,
                      label: (section as any).name,
                    }))
              }
              ButtonComponent={TertiaryButton}
            />
          </Row>
          {outboundErrorMessage && (
            <React.Fragment>
              <Space height={4} />
              <Row>
                <FieldInput.CaptionText isResponsive hasErrors>
                  {outboundErrorMessage}
                </FieldInput.CaptionText>
              </Row>
            </React.Fragment>
          )}
          <Space height={16} />
          <Button
            isResponsive
            text={'Call'}
            color={colors.green.status}
            onPress={handleSubmit}
            isSubmitting={submitting}
            iconLeft={Icon.PhoneAlt}
            isWidthOfContainer
          />
        </Column>
      </Drawer.FooterContainer>
    </React.Fragment>
  );
};

OutgoingCallDrawerContent.initialFragment = gql`
  fragment OutgoingCallDrawerContent_initial on User {
    outboundPhoneNumber {
      id
      number
    }
    verifiedPhoneNumbers {
      id
      number
      provider
    }
    viewingOrganization {
      id
      settings {
        id
        shouldDefaultOutboundNumberByAreaCode
      }
      verifiedBusinessPhoneNumbers {
        id
        number
        name
      }
    }
  }
`;

OutgoingCallDrawerContent.searchFragment = gql`
  ${OutgoingCallDrawerBody.fragment}

  fragment OutgoingCallDrawerContent_search on Organization {
    ...OutgoingCallDrawerBody
  }
`;

export default OutgoingCallDrawerContent;
